import isVizio from '@accedo/xdk-device-vizio-smartcast-contrib/esm/detection'
import { Lightning, Router } from '@lightningjs/sdk'

import { getColor, grey960, transparent } from '../../constants/colors'
import { LEFT_SAFE_ZONE_MARGIN } from '../../constants/general'
import { AppConfig } from '../../services/appConfig'
import {
  LinkModel,
  TvGridContainerItem,
  TvGridPartialModel,
  TvSearchLayoutModel,
} from '../../types/flo-experience'
import { toCardRef } from '../../utils/carouselUtils'
import { toRowRef } from '../../utils/gridUtils'
import { debounce } from '../../utils/misc'
import { captureScreenData, captureSearchQuery } from '../../utils/segmentAnalytics'
import { ActionButton } from '../button/ActionButton'
import CustomKeyboard from '../CustomKeyboard/CustomKeyboard'
import { PartialErrorMessage } from '../PartialErrorMessage/PartialErrorMessage'
import SearchInput from '../SearchInput/SearchInput'
import SearchGrid, { SearchGridStates } from './TvSearchGrid/TvSearchGrid'
import { RowItem, TvSearchGridRow } from './TvSearchGrid/TvSearchGridRow'

export enum SearchStates {
  Keyboard = 'Keyboard',
  Error = 'Error',
  ActionButton = 'ActionButton',
  SearchGrid = 'SearchGrid',
}

export const KEYBOARD_SIGNALS = {
  onKeyPress: 'onKeyPress',
  onClearPress: 'onClearPress',
  onBackspacePress: 'onBackspacePress',
}

const onSearchTermChange = 'onSearchTermChange'
const SEARCH_TERM_MAX_LENGTH = 1104
const SEARCH_DELAY = 1000
const SEARCH_PAGE_LIMIT = 24

export type layoutConfigType = {
  lastOffset: number
  lastRowIndex: number
  lastCardIndex: number
  lastSearchUrl: string
  lastState: string
  lastQuery: string
  lastTabIndex: number
  lastPartialSearchUrl: string
  refreshChildren?: boolean
  lastGridItems?: TvGridContainerItem[]
  pageNumber?: number
  paginationUrl: string
}

export default class TvSearchLayout extends Lightning.Component {
  private _data: TvSearchLayoutModel | null
  private _searchWithDelay: Function
  private _searchUrl: string
  private _isSearching = false
  private _partialSearchWithDelay: Function
  private _partialSearchUrl: string
  private _error = false

  static _template() {
    return {
      w: 1920,
      h: 1080,
      rect: true,
      color: getColor(grey960),
      Overlay: {
        w: (w) => w - (375 + LEFT_SAFE_ZONE_MARGIN), // 1276,
        h: 223,
        x: 375 + LEFT_SAFE_ZONE_MARGIN,
        rect: true,
        color: transparent,
        collision: true,
        zIndex: 2,
        Gradient: {
          flex: {
            direction: 'column',
          },
          w: (w) => w,
          h: 223,

          GradientTop: {
            rect: true,
            h: 210,
            color: getColor(grey960),
          },
          GradientBottom: {
            rect: true,
            h: 13,
            colorTop: getColor(grey960),
            colorBottom: transparent,
          },
        },
      },
      Container: {
        w: (w) => w,
        h: (h) => h,
        rect: true,
        color: transparent,
        flex: { direction: 'row' },
        LeftContainer: {
          y: 48,
          w: 375 + LEFT_SAFE_ZONE_MARGIN,
          zIndex: 4,
          rect: true,
          color: getColor(grey960),
          flex: {
            direction: 'column',
          },
          ActionButton: {
            x: LEFT_SAFE_ZONE_MARGIN,
            type: ActionButton,
            flexItem: { marginBottom: 32 },
          },
          Keyboard: {
            x: LEFT_SAFE_ZONE_MARGIN,
            type: CustomKeyboard,
            signals: {
              ...KEYBOARD_SIGNALS,
            },
          },
        },
        RightContainer: {
          y: 48,
          x: 48,
          w: 1228,
          flex: {
            direction: 'column',
          },
          SearchInput: {
            type: SearchInput,
            zIndex: 4,
            signals: {
              onSearchTermChange,
            },
          },
          Error: {
            y: 408,
            w: 1228,
            visible: false,
            type: PartialErrorMessage,
          },
          SearchGrid: {
            h: 847,
            type: SearchGrid,
          },
        },
      },
    }
  }

  get error() {
    return this.tag('Error') as PartialErrorMessage
  }

  get keyboard() {
    return this.tag('Keyboard') as CustomKeyboard
  }

  get actionButton() {
    return this.tag('ActionButton') as ActionButton
  }

  get searchInput() {
    return this.tag('SearchInput') as SearchInput
  }

  get searchGrid() {
    return this.tag('SearchGrid') as SearchGrid
  }

  get leftContainer() {
    return this.tag('LeftContainer')
  }

  get rightContainer() {
    return this.tag('RightContainer')
  }

  get data() {
    return this._data
  }

  get searchUrl() {
    return this._searchUrl
  }

  get partialSearchUrl() {
    return this._partialSearchUrl
  }

  get isSearching() {
    return this._isSearching
  }

  get lastLayoutConfig(): layoutConfigType {
    return {
      lastOffset: this.searchGrid.lastOffset,
      lastRowIndex: this.searchGrid.lastRowIndex,
      lastCardIndex: this.searchGrid.lastCardIndex,
      lastSearchUrl: this.searchUrl,
      lastState: this._getState(),
      lastQuery: this.searchInput.query,
      lastTabIndex: this.searchGrid.lastTabIndex,
      lastPartialSearchUrl: this.partialSearchUrl,
      lastGridItems: this.searchGrid.items,
      pageNumber: this.searchGrid.pageNumber,
      paginationUrl: this.searchGrid.paginationUrl,
    }
  }

  get isErrorSet() {
    return this._error
  }

  static _states() {
    return [
      class Keyboard extends this {
        $enter() {
          this.searchInput.toggleInput(true)
        }

        _getFocused() {
          return this.keyboard
        }

        _handleLeft() {
          if (Router.getActivePage().widgets.menu.visible) {
            Router.focusWidget('Menu')
          } else {
            return false
          }
        }

        _handleUp() {
          this._setState(SearchStates.ActionButton)
        }

        _handleRight() {
          if (this._error) {
            if (this.keyboard.index <= 1 && this.data?.tab_menu) {
              this.searchGrid._setState(SearchGridStates.Tabs)
              this._setState(SearchStates.SearchGrid)
            } else {
              this._setState(SearchStates.Error)
            }

            return
          }

          if (this.data?.tab_menu && this.keyboard.index <= 1) {
            this.searchGrid._setState(SearchGridStates.Tabs)
            this._setState(SearchStates.SearchGrid)
            return
          }

          if (!this.isSearching && !this.searchGrid.isEmpty) {
            this.searchGrid._setState(SearchGridStates.Grid)
            this._setState(SearchStates.SearchGrid)
          }
        }

        $exit() {
          this.searchInput.toggleInput(false)
        }
      },
      class Error extends this {
        _getFocused() {
          return this.error
        }

        _handleLeft() {
          this._setState(SearchStates.Keyboard)
        }

        _handleUp() {
          if (this.data?.tab_menu) {
            this.searchGrid._setState(SearchGridStates.Tabs)
            this._setState(SearchStates.SearchGrid)
          }
        }
      },
      class ActionButton extends this {
        _getFocused() {
          return this.actionButton
        }

        _handleLeft() {
          if (Router.getActivePage().widgets.menu.visible) {
            Router.focusWidget('Menu')
          } else {
            return false
          }
        }

        _handleDown() {
          this._setState(SearchStates.Keyboard)
        }

        _handleRight() {
          if (!this.isSearching && !this.searchGrid.isEmpty) {
            this._setState(SearchStates.SearchGrid)
          }
        }
      },

      class SearchGrid extends this {
        _getFocused() {
          return this.searchGrid
        }

        _handleLeft() {
          this._setState(SearchStates.Keyboard)
        }
      },
    ]
  }

  _init() {
    this._searchWithDelay = debounce(this.search.bind(this), SEARCH_DELAY)
    this._partialSearchWithDelay = debounce(this.partialSearch.bind(this), SEARCH_DELAY)
  }

  async initView(url: string, config?: layoutConfigType) {
    let partialData: TvGridPartialModel
    let query: string
    let pageSize: number

    if (url) {
      const decodedUrl = decodeURIComponent(url)
      const urlParams = new URLSearchParams(decodedUrl.split('?')[1])
      query = urlParams.get('query')
      pageSize = parseInt(urlParams.get('limit'), 0) ?? 0

      const data = await AppConfig.shared.experienceApi.getSearch(decodedUrl)
      this.correctSearchLimit(data?.search?.action, pageSize)

      if (data?.error) {
        this.fireAncestors('$connectionError', data.isNetworkError)
        return
      }

      this._data = data
      this._searchUrl = url
    } else {
      const data = await AppConfig.shared.experienceApi.getSearchRecommendations()

      if (data?.error) {
        this.fireAncestors('$connectionError', data.isNetworkError)
        return
      }

      this._data = data
    }

    void captureScreenData(this._data?.analytics)

    // Means a tab other than the first one was last selected, the backend
    // always provides the content of the first tab in the regular response
    if (config?.lastTabIndex > 0 && config?.lastPartialSearchUrl) {
      const urlParams = new URLSearchParams(decodeURIComponent(config?.lastPartialSearchUrl))
      pageSize = parseInt(urlParams.get('limit'), 0) ?? 0
      const data = await AppConfig.shared.experienceApi.getSearchPartial(
        config?.lastPartialSearchUrl,
      )

      if (data?.error) {
        this.fireAncestors('$connectionError', data.isNetworkError)
        return
      }

      partialData = data
      this._partialSearchUrl = config?.lastPartialSearchUrl
      this._data.content.items = partialData?.data?.items
    }

    if (config?.pageNumber > 0) {
      this.searchGrid.pageNumber = config.pageNumber
      this.searchGrid.paginationUrl = config?.paginationUrl

      if (config.refreshChildren) {
        // Used by the search screen where you add or remove favorites. We need to fetch the updated cards
        // for the current page, as they contain the updated api_call object

        const updatedPageData = await AppConfig.shared.experienceApi.getSearchPartial(
          config?.paginationUrl,
        )
        this._data.content.items = updatedPageData?.data?.items
      } else {
        this._data.content.items = config.lastGridItems
      }
    }

    if (config?.refreshChildren) {
      this.updateLayoutData(this._data, config)
    } else {
      this.renderLayoutData(this._data, config, pageSize)
    }

    if (query) {
      void captureSearchQuery(query, this._data?.analytics)
      this.searchInput.setQueryString(query)
    }

    this.fireAncestors('$ready')
  }

  async search(query: string) {
    if (!this._data) {
      this.setSearching(false)
      return
    }

    if (query === '') {
      this.clearAction()
      return
    }

    const { url: newSearchUrl, pageSize } = this.composeSearchUrl(query)
    const data = await AppConfig.shared.experienceApi.getSearch(newSearchUrl)
    this.correctSearchLimit(data?.search?.action, pageSize)

    if (data?.error) {
      this.setSearching(false)
      this.searchGrid.reset()
      this.error.toggleError((this._error = true), {
        isNetworkError: data.isNetworkError,
        retryAction: () => {
          this.error.toggleError((this._error = false))
          this._setState(SearchStates.Keyboard)
          this._searchWithDelay(query)
          this.setSearching(true)
        },
      })
      this.fireAncestors('$nextPageLoadComplete')
      return
    }

    this._data = data
    this._searchUrl = this._data?.search?.action?.url

    void captureScreenData(this._data?.analytics)
    void captureSearchQuery(query, this._data?.analytics)

    this.searchGrid.reset()
    this.actionButton.action = this._data?.action_button?.action
    this.searchGrid.pageSize = pageSize
    this.searchGrid.tabMenu.tabIndex = 0
    this.searchGrid.isCardFadeInEnabled = true
    this.searchGrid.data = this.data
    this.setSearching(false)
    this.fireAncestors('$nextPageLoadComplete')
  }

  async partialSearch(searchUrl: string) {
    const url = decodeURIComponent(searchUrl)
    const urlParams = new URLSearchParams(url)
    const pageSize = parseInt(urlParams.get('limit'), 0) ?? 0
    const partialResp = await AppConfig.shared.experienceApi.getSearchPartial(url)

    this._partialSearchUrl = searchUrl
    this.searchGrid.reset()

    if (partialResp?.error) {
      this.searchGrid.switchingTabs(false)
      this.error.toggleError((this._error = true), {
        isNetworkError: partialResp.isNetworkError,
        retryAction: () => {
          this.error.toggleError((this._error = false))
          this._partialSearchWithDelay(searchUrl)
          this.searchGrid.switchingTabs(true)
        },
      })
      this.fireAncestors('$nextPageLoadComplete')
      return
    }

    this.searchGrid.pageSize = pageSize
    this.searchGrid.renderGridRows(partialResp.data.items)
    this.searchGrid.switchingTabs(false)
    this._setState(SearchStates.SearchGrid)
  }

  async clearAction() {
    if (!this._data) return

    const url = this.data?.search?.clear_action?.url ?? this._data?.search?.action?.url
    const clearUrl = decodeURIComponent(url)
    this._data = await AppConfig.shared.experienceApi.getSearch(clearUrl)
    this._searchUrl = clearUrl
    this.actionButton.data = this._data?.action_button

    void captureScreenData(this._data?.analytics)

    this.searchGrid.reset()
    this.searchGrid.data = this.data
    this.setSearching(false)
    this.fireAncestors('$nextPageLoadComplete')
  }

  composeSearchUrl(query?: string) {
    const [path, queryString] = this._data.search.action.url.split('?')
    let params = new URLSearchParams(decodeURIComponent(queryString))
    if (query) {
      params.set('query', query)
    }

    const pageSize = parseInt(params.get('limit'), 0) ?? 0

    return {
      url: decodeURIComponent(`${path}?${params.toString()}`),
      pageSize,
    }
  }

  async onSearchTermChange(query: string) {
    this.error.toggleError((this._error = false))
    this._searchWithDelay(query)
    this.setSearching(true)
  }

  onKeyPress(key: string) {
    const query = this.searchInput.query

    if (this.searchInput.searchTerm.finalW < SEARCH_TERM_MAX_LENGTH) {
      this.searchInput.query = query + key
    }
  }

  async onClearPress() {
    this.searchInput.setQueryString('')
    await this.clearAction()
  }

  onBackspacePress() {
    const query = this.searchInput.query

    if (query.length > 0) {
      this.searchInput.query = query.slice(0, -1)
    } else if (isVizio) {
      this.fireAncestors('$handleBackAction')
    }
  }

  renderLayoutData(data: TvSearchLayoutModel, config: layoutConfigType, pageSize?: number) {
    this.searchInput.data = data?.search
    this.actionButton.data = data?.action_button
    this.searchGrid.tabMenu.tabIndex = config?.lastTabIndex ?? 0

    this.searchGrid.pageSize = pageSize
    this.searchGrid.isCardFadeInEnabled = !config?.refreshChildren
    this.searchGrid.data = data
    this.searchGrid.currentOffset = config?.lastOffset ?? 0
    this.searchGrid.currentRowIndex = config?.lastRowIndex ?? 0
    this.searchGrid.currentCardIndex = config?.lastCardIndex ?? 0

    if (config?.lastQuery) {
      this.searchInput.setQueryString(config.lastQuery)
    }

    if (config?.lastState === SearchStates.SearchGrid) {
      this._setState(SearchStates.SearchGrid)
    } else {
      this._setState(SearchStates.Keyboard)
    }
  }

  // Used for the favorites search only
  updateLayoutData(data: TvSearchLayoutModel, config: layoutConfigType) {
    // Convert data.content.items to a map, with id as the key
    const updatedContentsMap = data?.content?.items?.reduce((acc, item) => {
      acc[item.id] = item
      return acc
    }, {})

    const rowToUpdate = this.searchGrid.container.childList.getByRef(
      toRowRef(config.lastRowIndex),
    ) as TvSearchGridRow
    const cardToUpdate = rowToUpdate?.childList.getByRef(toCardRef(config.lastCardIndex)) as RowItem

    // Replace the api_call and trailing_badges object from card's data
    if (cardToUpdate && updatedContentsMap[cardToUpdate.data.id]) {
      cardToUpdate.data = updatedContentsMap[cardToUpdate.data.id]
      cardToUpdate.renderCardData()
    }
  }

  async $onTabChange(searchUrl: string) {
    this.error.toggleError((this._error = false))
    this._partialSearchWithDelay(searchUrl)
    this.searchGrid.switchingTabs(true)
    this.fireAncestors('$nextPageLoadComplete')
  }

  setSearching(isSearching: boolean) {
    this._isSearching = isSearching
    this.searchGrid.loadingResults(isSearching)
  }

  correctSearchLimit(searchAction: LinkModel, pageSize?: number) {
    if (searchAction?.url) {
      const [apiResource, searchParams] = searchAction.url.split('?')
      const urlSearchParams = new URLSearchParams(searchParams)

      if (!urlSearchParams.has('limit')) {
        const limit = pageSize ?? SEARCH_PAGE_LIMIT
        urlSearchParams.set('limit', limit.toString())
      }

      searchAction.url = `${apiResource}?${urlSearchParams.toString()}`
    }
  }
}
